import { fetchPlatformParameters } from "@origin-digital/event-dispatcher";
import {
  BackendCrm,
  CustomerType,
  StorageKeys,
} from "@origin-digital/platform-enums";

import {
  loadContentFromLocalStorage,
  loadContentFromCms,
  doFetch,
} from "./loader";
import bannerConfig from "./configurations.json";

/**
 * Compare a given time stamp with the time zone offset (example: 2018-11-20T07:30+11:00[Australia/Sydney]) with the local time
 * Return true if the given time if after the local time and false otherwise.
 * @param aedtTime: AEDT time stamp
 */

export interface CrisisBannerContent {
  start: string;
  end: string;
  type: BannerType;
  msg: string;
  heading: string;
  link: string;
  linkText: string;
  enabled: boolean | string;
  location: Array<BackendCrm | "ALL">;
}
export interface SecurityBannerContent {
  msgDefault: string;
  msgError: string;
  msgA: string;
  msgB: string;
  msgNB: string;
  enabled: boolean | string;
}
export type BannerId = StorageKeys.crisisBanner | StorageKeys.securitybanner;
export type BannerType = "warning" | "info";
export type BannerContentType = CrisisBannerContent | SecurityBannerContent;
export interface BannerInfo {
  id?: string;
  status: string;
  messageType?: string;
  content?: BannerContentType;
}

function isAfterLocalTime(aedtTime: string) {
  if (aedtTime) {
    const matchResult = aedtTime.match(/^(.*)\[.*\]$/);
    if (matchResult == null) {
      throw new Error(`isAfterLocalTime: dateTime '${aedtTime}' is not valid.`);
    }
    const [, time] = matchResult;
    const aedtTimeDate = new Date(time);

    if (isNaN(aedtTimeDate.getTime())) {
      throw new Error(`isAfterLocalTime: dateTime '${aedtTime}' is not valid.`);
    }

    if (aedtTimeDate.getTime() > Date.now()) {
      return true;
    } else {
      return false;
    }
  } else {
    throw new Error(`isAfterLocalTime: dateTime '${aedtTime}' is not valid.`);
  }
}

function getCookie(name) {
  return document.cookie
    .split(";")
    .filter((cookieStr) => cookieStr.includes(`${name}=`))[0];
}

function getCookieVal(name) {
  const cookie = getCookie(name);
  if (cookie) {
    return cookie.substring(cookie.indexOf("=") + 1);
  } else {
    return null;
  }
}

function getAccountId() {
  const myAccCH = window.ContextHub.getStore("originmyacc");
  const accountDetails = myAccCH
    ? myAccCH.getItem("originStore.accountDetails")
    : null;
  return accountDetails ? accountDetails.accountId : null;
}

function arrayEquals(a: any[], b: any[]) {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
}

function hasContentChanged(localContent: BannerInfo, cmsContent: BannerInfo) {
  if (!cmsContent.content || !localContent.content) {
    return true;
  }

  // cater for new items being added to content, force update
  if (
    Object.keys(localContent.content).length !==
    Object.keys(cmsContent.content).length
  ) {
    return true;
  }

  for (const key in localContent.content) {
    if (Array.isArray(localContent.content[key])) {
      if (!arrayEquals(localContent.content[key], cmsContent.content[key])) {
        return true;
      }
    } else if (localContent.content[key] !== cmsContent.content[key]) {
      return true;
    }
  }

  return false;
}

async function loadContent(
  bannerIdentifier: BannerId,
  preview?: boolean
): Promise<BannerInfo> {
  const localContent = loadContentFromLocalStorage(bannerIdentifier);
  const cmsContent = await loadContentFromCms(bannerIdentifier, preview);

  if (
    !localContent ||
    !localContent.content ||
    hasContentChanged(localContent, cmsContent)
  ) {
    setBannerInfoInLocalStorage(bannerIdentifier, cmsContent);
    return cmsContent;
  }

  return localContent;
}

function setBannerInfoInLocalStorage(
  bannerId: BannerId,
  bannerInfo: BannerInfo
) {
  localStorage.setItem(bannerId, JSON.stringify(bannerInfo));
}

function updateLocalStorage(key: BannerId, value) {
  const sessionStr = localStorage.getItem(key);
  const sessionObj = sessionStr ? JSON.parse(sessionStr) : {};
  const newSessionObj = { ...sessionObj, ...value };
  localStorage.setItem(key, JSON.stringify(newSessionObj));
}

export function hasMatchingBackend(
  location: CrisisBannerContent["location"],
  backends: BackendCrm[]
) {
  if (!location.length) return false;
  if (location.includes("ALL")) return true;

  if (!backends.length) return false;

  // @ts-ignore This is complaining that ALL does not exist on BackendCrm
  // but we are checking it on the previous line so we need to disable this type error
  return location.some((loc) => backends.includes(loc));
}

// if previewCrisisBanner is set in the URL then preview is true
export function isCrisisBannerPreview() {
  const urlParams = new URLSearchParams(window?.location?.search);
  const preview = urlParams.get("previewCrisisBanner");
  return preview === "" || preview === "true";
}

async function shouldShowCrisisBanner(content?: CrisisBannerContent) {
  if (!content) {
    return false;
  }

  if (isCrisisBannerPreview()) {
    return true;
  }

  if (!content.enabled) return false;

  const tokenResponse = await fetchPlatformParameters({});

  if (
    !tokenResponse ||
    tokenResponse.customerType === CustomerType.unauthenticated
  ) {
    return true;
  }

  return hasMatchingBackend(content.location, tokenResponse.backends);
}

async function getNotification() {
  const userToken = getCookieVal("user-token");
  if (!userToken) {
    throw new Error("User token cannot be found");
  }

  const { env } = window.oetal || {};
  const { name: envName = "local" } = env || {};
  const config = bannerConfig[envName];
  return doFetch(config.notificationApi, {
    method: "GET",
    cache: "no-cache",
    headers: new Headers({
      "Content-Type": "application/json",
      Authorization: userToken,
    }),
  });
}

export {
  getAccountId,
  getCookieVal,
  getNotification,
  hasContentChanged,
  isAfterLocalTime,
  loadContent,
  loadContentFromCms,
  loadContentFromLocalStorage,
  setBannerInfoInLocalStorage,
  shouldShowCrisisBanner,
  updateLocalStorage,
};
